import React from 'react';
import {
  Sparkles, CheckCircle2, RefreshCw, Save, ClipboardList, FileQuestion
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssessmentBlueprint } from './AssessmentBlueprint';

export function AssessmentTab({
  assessmentForm, setAssessmentForm, loading, generateAssessment,
  generatedAssessment, saveAssessment,
  getSubjectOptions, getYearOptions
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Assessment Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Topic *</Label>
            <Input
              placeholder="e.g., Food Groups"
              value={assessmentForm.topic}
              onChange={(e) => setAssessmentForm({...assessmentForm, topic: e.target.value})}
              data-testid="input-assessment-topic"
            />
          </div>

          <div className="space-y-2">
            <Label>Subject</Label>
            <Select
              value={assessmentForm.subject}
              onValueChange={(value) => setAssessmentForm({...assessmentForm, subject: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getSubjectOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Year Level</Label>
              <Select
                value={String(assessmentForm.year_level)}
                onValueChange={(value) => setAssessmentForm({...assessmentForm, year_level: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getYearOptions().map(year => (
                    <SelectItem key={year} value={String(year)}>Year {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={assessmentForm.assessment_type}
                onValueChange={(value) => setAssessmentForm({...assessmentForm, assessment_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quiz">Quiz</SelectItem>
                  <SelectItem value="test">Test</SelectItem>
                  <SelectItem value="exam">Exam</SelectItem>
                  <SelectItem value="worksheet">Worksheet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Difficulty</Label>
              <Select
                value={assessmentForm.difficulty}
                onValueChange={(value) => setAssessmentForm({...assessmentForm, difficulty: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Questions</Label>
              <Select
                value={String(assessmentForm.num_questions)}
                onValueChange={(value) => setAssessmentForm({...assessmentForm, num_questions: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 questions</SelectItem>
                  <SelectItem value="10">10 questions</SelectItem>
                  <SelectItem value="15">15 questions</SelectItem>
                  <SelectItem value="20">20 questions</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Question Types</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'multiple_choice', label: 'Multiple Choice' },
                { id: 'true_false', label: 'True / False' },
                { id: 'matching', label: 'Matching' },
                { id: 'fill_blank', label: 'Fill in the Blank' },
                { id: 'short_answer', label: 'Short Answer' },
              ].map(qt => (
                <label key={qt.id} className="flex items-center gap-2 p-2 rounded-md border hover:bg-gray-50 cursor-pointer" data-testid={`qtype-${qt.id}`}>
                  <input
                    type="checkbox"
                    checked={assessmentForm.question_types.includes(qt.id)}
                    onChange={(e) => {
                      const types = e.target.checked
                        ? [...assessmentForm.question_types, qt.id]
                        : assessmentForm.question_types.filter(t => t !== qt.id);
                      setAssessmentForm({...assessmentForm, question_types: types.length ? types : [qt.id]});
                    }}
                    className="rounded text-purple-600"
                  />
                  <span className="text-sm">{qt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <Button 
            className="w-full" 
            onClick={generateAssessment}
            disabled={loading}
            data-testid="btn-generate-assessment"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <ClipboardList className="h-4 w-4 mr-2" />
                Generate Assessment
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Generated Assessment</span>
            {generatedAssessment && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => saveAssessment(generatedAssessment)}
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!generatedAssessment ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileQuestion className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Generate quizzes, tests, and worksheets with answer keys</p>
            </div>
          ) : (
            <div className="space-y-6">
              <AssessmentBlueprint
                data={generatedAssessment}
                metadata={{
                  subject: assessmentForm.subject,
                  year_level: assessmentForm.year_level,
                  topic: assessmentForm.topic,
                  assessment_type: assessmentForm.assessment_type,
                }}
                type="assessment"
              />

              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">{generatedAssessment.title}</h3>
                <div className="flex gap-2">
                  <Badge>{generatedAssessment.total_marks} marks</Badge>
                  <Badge variant="outline">{generatedAssessment.time_limit} min</Badge>
                </div>
              </div>

              {generatedAssessment.instructions && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2">Instructions</h4>
                  <p className="text-sm text-blue-700">{generatedAssessment.instructions}</p>
                </div>
              )}

              <div className="space-y-4">
                <h4 className="font-semibold">Questions</h4>
                {generatedAssessment.questions?.map((q, idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded-lg border">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium">Q{q.number}. {q.question}</span>
                      <div className="flex gap-1">
                        <Badge variant="outline" className="text-xs capitalize">{(q.type || 'question').replace('_', ' ')}</Badge>
                        <Badge variant="secondary">{q.marks} mark{q.marks > 1 ? 's' : ''}</Badge>
                      </div>
                    </div>
                    {q.options && (
                      <div className="ml-4 space-y-1">
                        {q.options.map((opt, i) => (
                          <div key={i} className="text-sm text-muted-foreground">{opt}</div>
                        ))}
                      </div>
                    )}
                    {q.type === 'matching' && q.column_a && (
                      <div className="grid grid-cols-2 gap-4 mt-2 bg-white p-3 rounded border">
                        <div>
                          <strong className="text-sm text-blue-700">Column A:</strong>
                          <ul className="text-sm mt-1 space-y-1">{q.column_a.map((item, i) => <li key={i}>{i + 1}. {item}</li>)}</ul>
                        </div>
                        <div>
                          <strong className="text-sm text-green-700">Column B:</strong>
                          <ul className="text-sm mt-1 space-y-1">{q.column_b.map((item, i) => <li key={i}>{String.fromCharCode(65 + i)}. {item}</li>)}</ul>
                        </div>
                      </div>
                    )}
                    {q.type === 'fill_blank' && !q.options && (
                      <div className="ml-4 mt-2 text-sm text-muted-foreground italic">Write the missing word(s) in the blank.</div>
                    )}
                  </div>
                ))}
              </div>

              {generatedAssessment.answer_key && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Answer Key
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {generatedAssessment.answer_key.map((ans, idx) => (
                      <div key={idx} className="bg-green-50 p-2 rounded text-sm">
                        <strong>Q{ans.number}:</strong> {ans.answer}
                        {ans.explanation && (
                          <p className="text-xs text-green-700 mt-1">{ans.explanation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
